import { ShipInfo } from '@ais-ui/websockets';
import {
  VesselAdvancedIntegrity,
  VesselAdvancedVessel,
  VesselAdvancedRegister,
} from '@ais-ui/api-spec/shipdb';
import { LastPortCallData } from '../Targets/LastPortCall';

export interface SerializedShipInfo {
  name?: string;
  imo?: string;
  mmsi?: string;
  callSign?: string;
  country?: string;
  aisType?: string;
  sourceType?: string;
  shipType?: string;
  width?: string;
  length?: string;
  draught?: string;
  heading?: string;
  rateOfTurn?: string;
  courseOverGround?: string;
  speedOverGround?: string;
  destination?: string;
  navigationalStatus?: string;
  eta?: string | null;
  portEta?: string | null;
  lastAisLatitude?: string;
  lastAisLongitude?: string;
  lastAisReport?: string | null;
  lastAisSatLatitude?: string;
  lastAisSatLongitude?: string;
  lastAisSatReport?: string | null;
  latitudeDegrees?: string;
  longitudeDegrees?: string;
  latitude?: number;
  longitude?: number;
  cargoType?: string;
  lastOrigin?: string;
  posAccuracy?: string;
  flag?: string;
}

export interface PopupVesselPermissions {
  mdmIntegrity?: boolean;
  mdmAdvancedVesselInfo?: boolean;
  mdmRegister?: boolean;
}

export interface PopupPortCall {
  ata?: string;
  atd?: string;
  port?: {
    name: string;
    locode: string;
  };
}

export interface PopupShipTrack {
  coordinates: (string | number)[][];
}

export interface PopupVesselTimelineData {
  vessel: {
    imo: number;
    mmsi?: number;
    name?: string;
  };
  shipInfo?: SerializedShipInfo;
  lastPortCallData?: LastPortCallData | null;
  portCalls: PopupPortCall[];
  advancedIntegrity?: VesselAdvancedIntegrity;
  advancedVessel?: VesselAdvancedVessel;
  advancedRegister?: VesselAdvancedRegister;
  shipTrack?: PopupShipTrack;
  dateRange: {
    from: string;
    to: string;
  };
  permissions: PopupVesselPermissions;
  error?: string;
  /** Unique key for this data snapshot - used to determine if update is needed */
  dataKey?: string;
  /** Unique key for chart data - used to trigger chart re-render on date range changes */
  chartDataKey?: string;
}

export const deserializeShipInfo = (
  serialized: SerializedShipInfo | undefined,
): ShipInfo | null => {
  if (!serialized) return null;

  return {
    name: serialized.name || '',
    imo: serialized.imo || '',
    mmsi: serialized.mmsi || '',
    callSign: serialized.callSign || '',
    country: serialized.country || '',
    aisType: serialized.aisType || '',
    sourceType: serialized.sourceType || '',
    shipType: serialized.shipType || '',
    width: serialized.width || '',
    length: serialized.length || '',
    draught: serialized.draught || '',
    heading: serialized.heading || '',
    rateOfTurn: serialized.rateOfTurn || '',
    courseOverGround: serialized.courseOverGround || '',
    speedOverGround: serialized.speedOverGround || '',
    destination: serialized.destination || '',
    navigationalStatus: serialized.navigationalStatus || '',
    eta: serialized.eta ? new Date(serialized.eta) : null,
    portEta: serialized.portEta ? new Date(serialized.portEta) : null,
    lastAisLatitude: serialized.lastAisLatitude || '',
    lastAisLongitude: serialized.lastAisLongitude || '',
    lastAisReport: serialized.lastAisReport
      ? new Date(serialized.lastAisReport)
      : null,
    lastAisSatLatitude: serialized.lastAisSatLatitude || '',
    lastAisSatLongitude: serialized.lastAisSatLongitude || '',
    lastAisSatReport: serialized.lastAisSatReport
      ? new Date(serialized.lastAisSatReport)
      : null,
    latitudeDegrees: serialized.latitudeDegrees || '',
    longitudeDegrees: serialized.longitudeDegrees || '',
    latitude: serialized.latitude || 0,
    longitude: serialized.longitude || 0,
    cargoType: serialized.cargoType || '',
    lastOrigin: serialized.lastOrigin || '',
    posAccuracy: serialized.posAccuracy || '',
    flag: serialized.flag || '',
  };
};

export const serializeShipInfo = (shipInfo: any): SerializedShipInfo => {
  return {
    name: shipInfo?.name,
    imo: shipInfo?.imo,
    mmsi: shipInfo?.mmsi,
    callSign: shipInfo?.callSign,
    country: shipInfo?.country,
    aisType: shipInfo?.aisType,
    sourceType: shipInfo?.sourceType,
    shipType: shipInfo?.shipType,
    width: shipInfo?.width,
    length: shipInfo?.length,
    draught: shipInfo?.draught,
    heading: shipInfo?.heading,
    rateOfTurn: shipInfo?.rateOfTurn,
    courseOverGround: shipInfo?.courseOverGround,
    speedOverGround: shipInfo?.speedOverGround,
    destination: shipInfo?.destination,
    navigationalStatus: shipInfo?.navigationalStatus,
    eta: shipInfo?.eta?.toISOString?.() || shipInfo?.eta,
    portEta: shipInfo?.portEta?.toISOString?.() || shipInfo?.portEta,
    lastAisLatitude: shipInfo?.lastAisLatitude,
    lastAisLongitude: shipInfo?.lastAisLongitude,
    lastAisReport:
      shipInfo?.lastAisReport?.toISOString?.() || shipInfo?.lastAisReport,
    lastAisSatLatitude: shipInfo?.lastAisSatLatitude,
    lastAisSatLongitude: shipInfo?.lastAisSatLongitude,
    lastAisSatReport:
      shipInfo?.lastAisSatReport?.toISOString?.() || shipInfo?.lastAisSatReport,
    latitudeDegrees: shipInfo?.latitudeDegrees,
    longitudeDegrees: shipInfo?.longitudeDegrees,
    latitude: shipInfo?.latitude,
    longitude: shipInfo?.longitude,
    cargoType: shipInfo?.cargoType,
    lastOrigin: shipInfo?.lastOrigin,
    posAccuracy: shipInfo?.posAccuracy,
    flag: shipInfo?.flag,
  };
};
